import { useCallback, useEffect, useState } from 'react';
import type { AgentRow, Api, Campaign, CampaignTarget, Contact, Offering } from '../api';
import { agentTitle } from '../api';
import type { T } from '../i18n';
import { PERM, useCan } from '../access';
import { Field, ReadOnlyNote } from '../ui';
import { errorText } from './AgentSettings';
import { when } from './Calls';

type Go = (route: 'campaigns' | 'campaign' | 'call' | 'contacts' | 'agent', params?: Record<string, string>) => void;

const progress = (c: Campaign): number => (c.stats.total ? Math.round(((c.stats.done + c.stats.failed + c.stats.skipped) / c.stats.total) * 100) : 0);
const statusKind = (s: Campaign['status']): string => (s === 'running' ? 'live' : s === 'paused' ? 'changed' : s === 'done' ? '' : 'draft');

/** Campaigns (ADR-028): an agent calling a list, with retries, within business hours. */
export const CampaignsPage = ({ api, t, go }: { api: Api; t: T; go: Go }) => {
  // ADR-071: creating, starting, pausing and cancelling a campaign need centeros.campaigns; the list is for everyone who may see.
  const canRun = useCan()(PERM.campaigns);
  const [rows, setRows] = useState<Campaign[]>();
  const [wizard, setWizard] = useState(false);
  const [error, setError] = useState<string>();
  const load = useCallback(() => {
    api.campaigns().then((r) => setRows(r.campaigns)).catch((e: unknown) => setError(errorText(t, e)));
  }, [api, t]);
  useEffect(load, [load]);

  if (wizard && canRun) return <CampaignWizard api={api} t={t} onCancel={() => setWizard(false)} onCreated={(id) => go('campaign', { id })} go={go} />;
  return (
    <section>
      <header className="page-head">
        <div>
          <h1>{t('campaigns.title')}</h1>
          <p className="muted">{t('campaigns.subtitle')}</p>
        </div>
        {canRun && <button type="button" className="primary" onClick={() => setWizard(true)}>+ {t('campaigns.new')}</button>}
      </header>
      <ReadOnlyNote t={t} need={PERM.campaigns} />
      {error && <div className="banner error">{error}</div>}
      {rows && rows.length === 0 && (
        <div className="empty">
          <p>{t('campaigns.empty')}</p>
          {canRun && <button type="button" className="primary" onClick={() => setWizard(true)}>+ {t('campaigns.new')}</button>}
        </div>
      )}
      {rows && rows.length > 0 && (
        <div className="list">
          {rows.map((c) => (
            <div key={c.id} className="list-row clickable" onClick={() => go('campaign', { id: c.id })}>
              <div className="grow">
                <div className="row"><span className="title">{c.name}</span><span className={`status ${statusKind(c.status)}`}>{t(`campaigns.status.${c.status}`)}</span></div>
                <div className="sub">{c.agentName} · {t('campaigns.targets', { n: c.stats.total })} · {when(c.createdAt)}</div>
                <div className="bar campaign-bar"><div style={{ width: `${progress(c)}%` }} /></div>
              </div>
              <div className="campaign-mini">
                <span><strong>{c.stats.done}</strong> {t('campaigns.stat.done')}</span>
                <span><strong>{c.stats.pending + c.stats.dialing}</strong> {t('campaigns.stat.left')}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const CampaignWizard = ({ api, t, onCancel, onCreated, go }: { api: Api; t: T; onCancel: () => void; onCreated: (id: string) => void; go: Go }) => {
  // ADR-071: a guard only — the wizard is not offered without centeros.campaigns.
  const canRun = useCan()(PERM.campaigns);
  const [step, setStep] = useState(1);
  const [agents, setAgents] = useState<AgentRow[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [name, setName] = useState('');
  const [agentId, setAgentId] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [q, setQ] = useState('');
  const [concurrency, setConcurrency] = useState(1);
  const [maxAttempts, setMaxAttempts] = useState(2);
  const [retryMinutes, setRetryMinutes] = useState(60);
  // ADR-041: the chosen agent's offerings; the ones ticked go first in this campaign's calls.
  const [offerings, setOfferings] = useState<Offering[]>([]);
  const [focus, setFocus] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    api.agents().then((r) => {
      const live = r.items.filter((a) => a.publishedPackageId && a.directionDefault !== 'inbound');
      setAgents(live);
      if (live[0]) setAgentId(live[0].id);
    }).catch(() => undefined);
    api.contacts('', 5000).then((r) => {
      setContacts(r.contacts);
      setPicked(new Set(r.contacts.map((c) => c.id)));
    }).catch(() => undefined);
  }, [api]);

  useEffect(() => {
    setFocus([]);
    if (!agentId) return setOfferings([]);
    api.agent(agentId).then((a) => setOfferings(((a.spec?.spec.offerings as Offering[] | undefined) ?? []))).catch(() => setOfferings([]));
  }, [api, agentId]);

  const shown = contacts.filter((c) => !q || c.name.toLowerCase().includes(q.toLowerCase()) || c.phone.includes(q));
  const toggle = (id: string) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const ready = [name.trim() && agentId, picked.size > 0, true, true][step - 1];

  const create = async (start: boolean) => {
    setBusy(true);
    setError(undefined);
    try {
      const r = await api.createCampaign({ name: name.trim(), agentId, contactIds: [...picked], concurrency, maxAttempts, retryMinutes, focusOfferings: focus });
      if (start) await api.campaignAction(r.id, 'start');
      onCreated(r.id);
    } catch (e) {
      setError(errorText(t, e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <button type="button" className="link back" onClick={onCancel}>← {t('campaigns.title')}</button>
      <h1>{t('campaigns.new')}</h1>
      <div className="path wizard-steps">
        {[1, 2, 3, 4].map((s) => (
          <span key={s} className={`path-step ${s === step ? 'current' : ''}`}><span className="step-no">{s}</span>{t(`campaigns.step.${s}`)}</span>
        ))}
      </div>
      <div className="card">
        {step === 1 && (
          <div className="section-body">
            <Field label={t('campaigns.name')}><input value={name} autoFocus placeholder={t('campaigns.name.placeholder')} onChange={(e) => setName(e.target.value)} /></Field>
            <Field label={t('campaigns.agent')} hint={agents.length === 0 ? t('campaigns.agent.none') : t('campaigns.agent.hint')}>
              <select value={agentId} onChange={(e) => setAgentId(e.target.value)}>
                {agents.map((a) => <option key={a.id} value={a.id}>{agentTitle(a)}</option>)}
              </select>
            </Field>
          </div>
        )}
        {step === 2 && (
          <div className="section-body">
            {contacts.length === 0 && (
              <div className="empty"><p>{t('campaigns.contacts.none')}</p><button type="button" className="primary" onClick={() => go('contacts')}>{t('campaigns.contacts.go')}</button></div>
            )}
            {contacts.length > 0 && (
              <>
                <div className="row-between">
                  <input className="search" type="search" value={q} placeholder={t('contacts.search')} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 280 }} />
                  <span className="row">
                    <span className="hint">{t('campaigns.picked', { n: picked.size, total: contacts.length })}</span>
                    <button type="button" className="ghost" onClick={() => setPicked(new Set(contacts.map((c) => c.id)))}>{t('campaigns.all')}</button>
                    <button type="button" className="ghost" onClick={() => setPicked(new Set())}>{t('campaigns.none')}</button>
                  </span>
                </div>
                <div className="list voice-scroll">
                  {shown.map((c) => (
                    <label key={c.id} className="list-row clickable">
                      <input type="checkbox" checked={picked.has(c.id)} onChange={() => toggle(c.id)} />
                      <span className="grow"><span className="title">{c.name || '—'}</span><span className="sub mono"> {c.phone}</span></span>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        {step === 3 && (
          <div className="section-body">
            <Field label={t('campaigns.concurrency')} hint={t('campaigns.concurrency.hint')}>
              <select value={concurrency} onChange={(e) => setConcurrency(Number(e.target.value))}>{[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}</select>
            </Field>
            <div className="form-grid">
              <Field label={t('campaigns.attempts')} hint={t('campaigns.attempts.hint')}>
                <select value={maxAttempts} onChange={(e) => setMaxAttempts(Number(e.target.value))}>{[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}</select>
              </Field>
              <Field label={t('campaigns.retry')}>
                <select value={retryMinutes} onChange={(e) => setRetryMinutes(Number(e.target.value))}>
                  {[15, 30, 60, 120, 240, 1440].map((m) => <option key={m} value={m}>{m < 60 ? t('campaigns.minutes', { n: m }) : m < 1440 ? t('campaigns.hours', { n: m / 60 }) : t('campaigns.day')}</option>)}
                </select>
              </Field>
            </div>
            <p className="hint">{t('campaigns.hours.note')}</p>
            <Field label={t('campaigns.focus')} hint={offerings.length ? t('campaigns.focus.hint') : t('campaigns.focus.none')}>
              <div className="focus-list">
                {offerings.map((o, i) => (
                  <label key={o.id} className="list-row clickable">
                    <input type="checkbox" checked={focus.includes(o.id)} onChange={() => setFocus((f) => (f.includes(o.id) ? f.filter((x) => x !== o.id) : [...f, o.id]))} />
                    <span className="grow"><span className="title">{i + 1}. {o.name}</span><span className="sub"> — {o.summary}</span></span>
                  </label>
                ))}
              </div>
            </Field>
          </div>
        )}
        {step === 4 && (
          <div className="section-body">
            <dl className="facts">
              <div><dt>{t('campaigns.name')}</dt><dd>{name}</dd></div>
              <div><dt>{t('campaigns.agent')}</dt><dd>{((a) => (a ? agentTitle(a) : ''))(agents.find((a) => a.id === agentId))}</dd></div>
              <div><dt>{t('campaigns.step.2')}</dt><dd>{picked.size}</dd></div>
              <div><dt>{t('campaigns.focus')}</dt><dd>{focus.length ? focus.map((id) => offerings.find((o) => o.id === id)?.name).join(', ') : t('campaigns.focus.default')}</dd></div>
              <div><dt>{t('campaigns.concurrency')}</dt><dd>{concurrency}</dd></div>
              <div><dt>{t('campaigns.attempts')}</dt><dd>{maxAttempts}</dd></div>
            </dl>
            <div className="banner warn">{t('campaigns.confirm', { n: picked.size })}</div>
          </div>
        )}
        {error && <div className="banner error small">{error}</div>}
        <div className="row-between" style={{ marginTop: 16 }}>
          <button type="button" className="ghost" onClick={() => (step === 1 ? onCancel() : setStep(step - 1))}>{step === 1 ? t('common.cancel') : t('common.back')}</button>
          {step < 4 ? (
            <button type="button" className="primary" disabled={!ready} onClick={() => setStep(step + 1)}>{t('call.next')}</button>
          ) : (
            <span className="row">
              <button type="button" className="secondary" disabled={busy || !canRun} title={canRun ? undefined : t('error.PERMISSION_DENIED')} onClick={() => void create(false)}>{t('campaigns.saveDraft')}</button>
              <button type="button" className="primary" disabled={busy || !canRun} title={canRun ? undefined : t('error.PERMISSION_DENIED')} onClick={() => void create(true)}>{busy ? t('settings.saving') : t('campaigns.createStart')}</button>
            </span>
          )}
        </div>
      </div>
    </section>
  );
};

const resultLabel = (t: T, x: CampaignTarget): string => {
  const k = x.result?.kind;
  if (!k) return '';
  const base = t(`campaigns.result.${k}`) === `campaigns.result.${k}` ? k : t(`campaigns.result.${k}`);
  if (k === 'callback' && x.result?.when) return `${base}: ${x.result.when}`;
  if ((k === 'skipped' || k === 'retry') && x.result?.reason) return `${base} (${t(`error.${x.result.reason}`) === `error.${x.result.reason}` ? x.result.reason : t(`error.${x.result.reason}`)})`;
  return base;
};

export const CampaignPage = ({ api, t, id, go }: { api: Api; t: T; id: string; go: Go }) => {
  // ADR-071: start / resume / pause / cancel need centeros.campaigns; status, numbers and targets are for everyone who may see.
  const canRun = useCan()(PERM.campaigns);
  const [c, setC] = useState<Campaign & { targets: CampaignTarget[] }>();
  const [error, setError] = useState<string>();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.campaign(id).then((r) => setC(r.campaign)).catch((e: unknown) => setError(errorText(t, e)));
  }, [api, id, t]);
  useEffect(load, [load]);
  useEffect(() => {
    if (c?.status !== 'running') return undefined;
    const tick = setInterval(load, 10_000);
    return () => clearInterval(tick);
  }, [c?.status, load]);

  const act = async (a: 'start' | 'pause' | 'cancel') => {
    setBusy(true);
    setError(undefined);
    try {
      await api.campaignAction(id, a);
      setConfirming(false);
      load();
    } catch (e) {
      setError(errorText(t, e));
    } finally {
      setBusy(false);
    }
  };
  if (!c) return error ? <div className="banner error">{error}</div> : <div className="muted">{t('boot.loading')}</div>;
  const left = c.stats.pending + c.stats.dialing;
  return (
    <section>
      <button type="button" className="link back" onClick={() => go('campaigns')}>← {t('campaigns.title')}</button>
      <header className="page-head">
        <div>
          <div className="head-title"><h1>{c.name}</h1><span className={`status ${statusKind(c.status)}`}>{t(`campaigns.status.${c.status}`)}</span></div>
          <div className="head-meta"><span>{c.agentName}</span><span>·</span><span>{t('campaigns.settings', { c: c.concurrency, a: c.maxAttempts, r: c.retryMinutes })}</span></div>
        </div>
        {canRun && (
        <div className="head-actions">
          {(c.status === 'draft' || c.status === 'paused') && !confirming && <button type="button" className="primary" onClick={() => setConfirming(true)}>▶ {t(c.status === 'draft' ? 'campaigns.start' : 'campaigns.resume')}</button>}
          {c.status === 'running' && <button type="button" className="secondary" disabled={busy} onClick={() => void act('pause')}>❚❚ {t('campaigns.pause')}</button>}
          {c.status !== 'done' && c.status !== 'cancelled' && <button type="button" className="ghost" disabled={busy} onClick={() => void act('cancel')}>{t('campaigns.cancel')}</button>}
        </div>
        )}
      </header>
      <ReadOnlyNote t={t} need={PERM.campaigns} />
      {canRun && confirming && (
        <div className="banner warn">
          <span>{t('campaigns.confirm', { n: left })}</span>
          <span className="row"><button type="button" className="primary" disabled={busy} onClick={() => void act('start')}>{t('campaigns.confirm.yes')}</button><button type="button" className="ghost" onClick={() => setConfirming(false)}>{t('common.cancel')}</button></span>
        </div>
      )}
      {error && <div className="banner error">{error}</div>}
      <div className="kpis">
        {(['total', 'pending', 'dialing', 'done', 'failed', 'skipped'] as const).map((k) => (
          <div key={k} className={`kpi static ${k === 'done' ? 'ok' : k === 'failed' ? 'bad' : ''}`}><span className="kpi-label">{t(`campaigns.stat.${k}`)}</span><span className="kpi-value">{c.stats[k]}</span></div>
        ))}
      </div>
      <div className="bar campaign-bar big"><div style={{ width: `${progress(c)}%` }} /></div>
      <table className="table section-gap">
        <thead><tr><th>{t('contacts.col.name')}</th><th>{t('calls.col.number')}</th><th>{t('campaigns.col.status')}</th><th>{t('campaigns.col.attempts')}</th><th>{t('calls.col.result')}</th></tr></thead>
        <tbody>
          {c.targets.map((x) => (
            <tr key={x.id} className={x.lastCallId ? 'clickable' : ''} onClick={() => x.lastCallId && go('call', { id: x.lastCallId })}>
              <td>{x.name || '—'}</td>
              <td className="mono">{x.phone}</td>
              <td><span className={`pill ${x.status === 'done' ? 'published' : x.status === 'failed' ? 'fail' : x.status === 'dialing' ? 'published' : 'muted'}`}>{t(`campaigns.target.${x.status}`)}</span>{x.status === 'pending' && x.attempts > 0 && <span className="hint"> · {when(x.nextAttemptAt)}</span>}</td>
              <td>{x.attempts}/{c.maxAttempts}</td>
              <td>{resultLabel(t, x)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
};
